import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import {
  FaMoneyBillWave,
  FaWallet,
  FaQrcode,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaShieldAlt,
  FaSync,
} from "react-icons/fa";

const CODPaymentModal = ({
  isOpen,
  onClose,
  onConfirm,
  bookingDetails,
  totalAmount,
  isSubmitting = false,
  authHeaders = null, // driver auth headers for OTP generation API call
  extraKm = 0,
  extraKmCharge = 0,
  waitingTime = 0,
  waitingCharge = 0,
}) => {
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [isRazorpaySimulated, setIsRazorpaySimulated] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrSource, setQrSource] = useState("");

  // Wallet OTP state
  const [walletOtp, setWalletOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpCountdown, setOtpCountdown] = useState(0);
  const countdownRef = useRef(null);

  const amountToCollect = bookingDetails
    ? (totalAmount || bookingDetails.finalAmount || bookingDetails.remainingAmount || bookingDetails.totalAmount || 0)
    : 0;

  const displayExtraKmVal = Number(extraKm || bookingDetails?.extraKm || 0);
  const displayExtraKm = Number(extraKmCharge || bookingDetails?.extraKmCharge || 0);
  const displayWaitingTime = Number(waitingTime || bookingDetails?.waitingTime || 0);
  const displayWaitingCharge = Number(waitingCharge || bookingDetails?.waitingCharge || 0);

  const companyUpiId = process.env.REACT_APP_COMPANY_UPI_ID || "cabzigo@ybl";
  const isRazorpaySource = qrSource === "razorpay";

  // ── Countdown timer ──────────────────────────────────────────────────────────
  const startCountdown = (seconds = 60) => {
    setOtpCountdown(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (countdownRef.current) clearInterval(countdownRef.current); }, []);

  // Reset OTP state when modal closes or method changes away from WALLET
  useEffect(() => {
    if (!isOpen || paymentMethod !== "WALLET") {
      setWalletOtp("");
      setOtpRequested(false);
      setOtpError("");
      setOtpCountdown(0);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  }, [isOpen, paymentMethod]);

  // ── Request OTP ──────────────────────────────────────────────────────────────
  const handleRequestOtp = async () => {
    if (!bookingDetails?._id) return;
    setOtpLoading(true);
    setOtpError("");
    try {
      await axios.post(
        `${API_URL}/booking/${bookingDetails._id}/generate-wallet-otp`,
        {},
        { headers: authHeaders || {} }
      );
      setOtpRequested(true);
      setWalletOtp("");
      startCountdown(60);
    } catch (err) {
      setOtpError(err.response?.data?.message || "Failed to generate OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── QR code for ONLINE ───────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    if (isOpen && paymentMethod === "ONLINE" && bookingDetails?._id) {
      const fetchQrCode = async () => {
        setQrLoading(true);
        setQrSource("");
        try {
          const response = await axios.post(`${API_URL}/payment/generate-booking-qr`, {
            bookingId: bookingDetails.bookingId || bookingDetails._id,
            amount: amountToCollect,
          });
          if (isMounted) {
            if (response.data?.success) {
              setQrCodeUrl(response.data.qrCodeUrl);
              setQrSource(response.data.source || "");
            } else throw new Error("Invalid response");
          }
        } catch {
          if (isMounted) {
            const upiPayUrl = `upi://pay?pa=${companyUpiId}&pn=${encodeURIComponent("CabZigo")}&am=${amountToCollect.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Booking_${bookingDetails.bookingId || bookingDetails._id}`)}`;
            setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=10&data=${encodeURIComponent(upiPayUrl)}`);
            setQrSource("client_fallback");
          }
        } finally {
          if (isMounted) setQrLoading(false);
        }
      };
      fetchQrCode();
    }
    return () => { isMounted = false; };
  }, [isOpen, paymentMethod, bookingDetails?._id, bookingDetails?.bookingId, amountToCollect, companyUpiId]);

  useEffect(() => {
    if (!isOpen || paymentMethod !== "ONLINE") {
      setQrCodeUrl("");
      setQrLoading(false);
      setQrSource("");
    }
  }, [isOpen, paymentMethod]);

  if (!isOpen || !bookingDetails || typeof document === "undefined") return null;

  const isCorp = !!(bookingDetails?.corporateAccountId || bookingDetails?.corporateMemberId || ["corporate_admin", "corporate_staff"].includes(bookingDetails?.createdByType));
  const isCompanyPaid = isCorp && (bookingDetails?.paymentMode === "COMPANY" || (bookingDetails?.paymentMethod && bookingDetails?.paymentMethod?.toUpperCase() === "COMPANY"));

  let walletLabel = "User Wallet Payment";
  let walletDescription = "Debit from passenger wallet. OTP verification required.";
  let otpRecipientNote = "a secure code will be sent to the passenger's app";
  let passengerWalletBalance = 0;

  if (isCorp) {
    if (isCompanyPaid) {
      walletLabel = "Company Wallet Payment";
      walletDescription = "Billed to the corporate company account. OTP sent to the corporate admin — ask them for it.";
      otpRecipientNote = "a secure code will be sent to the corporate admin's app";

      const corpAccount = bookingDetails?.corporateAccountId;
      if (corpAccount) {
        const companyWalletBalance = Math.max(0, (corpAccount.walletBalance || 0) - (corpAccount.creditUsed || 0));
        const companyAvailableCredit = Math.max(0, (corpAccount.creditLimit || 0) - (corpAccount.creditUsed || 0));
        passengerWalletBalance = corpAccount.billingModel === 'wallet' ? companyWalletBalance : companyAvailableCredit;
      }
    } else {
      walletLabel = "Corporate Member Wallet";
      walletDescription = "Debit from staff member's personal wallet. OTP sent to the staff member — ask them for it.";
      otpRecipientNote = "a secure code will be sent to the staff member's app";
      passengerWalletBalance = bookingDetails?.corporateMemberId?.walletBalance ?? 0;
    }
  } else {
    passengerWalletBalance = bookingDetails?.user?.walletBalance ?? 0;
  }

  const isWalletSufficient = passengerWalletBalance >= amountToCollect;
  const passengerName = bookingDetails?.user?.name || bookingDetails?.passengerDetails?.name || (isCorp ? bookingDetails?.corporateMemberId?.fullName : "Passenger");

  // Detect full payment rides: paid at booking time via ONLINE/WALLET/COMPANY (not COD).
  // Razorpay/online full payments may not set advancePaid, so paymentMethod is the source of truth.
  const remainingAmountRaw = bookingDetails?.remainingAmount !== undefined && bookingDetails?.remainingAmount !== null
    ? bookingDetails.remainingAmount
    : ((bookingDetails?.totalAmount || bookingDetails?.amount || 0) - (bookingDetails?.advancePaid || 0));
  const isFullyPaid =
    bookingDetails?.paymentMethod?.toLowerCase() !== 'cod' &&
    bookingDetails?.paymentStatus === 'completed' &&
    remainingAmountRaw <= 0;
  const originalPaymentMethod = bookingDetails?.paymentMethod?.toUpperCase();

  const handleConfirm = () => {
    onConfirm({
      method: paymentMethod,
      amount: amountToCollect,
      bookingId: bookingDetails._id,
      // include OTP only for wallet payments
      walletOtp: paymentMethod === "WALLET" ? walletOtp.trim() : undefined,
    });
  };

  const isConfirmDisabled =
    isSubmitting ||
    (paymentMethod === "ONLINE" && !isRazorpaySimulated) ||
    (paymentMethod === "WALLET" && (!otpRequested || walletOtp.trim().length !== 6));

  return createPortal(
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
      <div className="bg-slate-900 border border-slate-800 text-white rounded-2xl shadow-2xl max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="p-6">

          {/* Header */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-800">
            <h2 className="text-xl font-bold text-slate-100 tracking-tight">Collect Payment</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-200 transition-colors" disabled={isSubmitting}>
              <FaTimes size={20} />
            </button>
          </div>

          {/* Booking Details */}
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Booking Info</span>
              <span className="text-xs font-bold text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                {bookingDetails.bookingId}
              </span>
            </div>
            <div className="text-sm space-y-2.5">
              <div className="flex justify-between">
                <span className="text-slate-400">Passenger:</span>
                <span className="font-medium text-slate-200">{passengerName}</span>
              </div>
              {(bookingDetails.advancePaid > 0 || displayExtraKm > 0 || displayWaitingCharge > 0) && (
                <div className="border-t border-slate-800/60 pt-2.5 space-y-1.5 text-xs text-slate-400">
                  <div className="flex justify-between">
                    <span>Total Ride Amount:</span>
                    <span>₹{bookingDetails.totalAmount || bookingDetails.amount || 0}</span>
                  </div>
                  {bookingDetails.advancePaid > 0 && (
                    <div className="flex justify-between text-emerald-400">
                      <span>Advance Paid:</span>
                      <span>-₹{bookingDetails.advancePaid}</span>
                    </div>
                  )}
                  {displayExtraKm > 0 && (
                    <div className="flex justify-between text-amber-400">
                      <span>Extra KM ({displayExtraKmVal.toFixed(1)} km):</span>
                      <span>+₹{displayExtraKm.toFixed(2)}</span>
                    </div>
                  )}
                  {displayWaitingCharge > 0 && (
                    <div className="flex justify-between text-amber-400">
                      <span>Extra Waiting ({displayWaitingTime} min):</span>
                      <span>+₹{displayWaitingCharge.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between items-center border-t border-slate-800 pt-3 mt-2">
                <span className="text-sm font-bold text-slate-300">
                  {isFullyPaid ? "Extra Charges to Collect:" : "Final Amount to Collect:"}
                </span>
                <span className={`text-xl font-black ${isFullyPaid ? "text-amber-400" : "text-rose-500"}`}>
                  ₹{amountToCollect.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Full Payment Info Banner — shown when ride fare was pre-paid online/wallet */}
          {isFullyPaid && (
            <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-xl p-4 mb-5 flex items-start gap-3">
              <span className="text-emerald-400 text-lg shrink-0">✅</span>
              <div>
                <p className="text-sm font-bold text-emerald-300">
                  {originalPaymentMethod === "WALLET"
                    ? "Full Payment — Paid via Wallet"
                    : "Full Payment — Paid Online"}
                </p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  The ride fare has been paid in full. Collect{" "}
                  <span className="font-bold text-white">only the extra charges</span>{" "}
                  (waiting time + extra KM) shown above.
                </p>
              </div>
            </div>
          )}

          {/* Payment Method Selector */}
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">
            {isFullyPaid ? "Collect Extra Charges Via" : "Select Payment Method"}
          </h3>
          <div className="space-y-3 mb-6">

            {/* COD */}
            <div
              className={`border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                paymentMethod === "COD"
                  ? "border-emerald-500 bg-emerald-950/20"
                  : "border-slate-800 bg-slate-900/50 hover:border-slate-700"
              }`}
              onClick={() => { setPaymentMethod("COD"); setIsRazorpaySimulated(false); }}
            >
              <div className="flex items-start">
                <div className="bg-emerald-500/10 p-2.5 rounded-lg border border-emerald-500/20 text-emerald-400 mr-4">
                  <FaMoneyBillWave size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-100 text-sm">Cash on Delivery (COD)</h4>
                  <p className="text-xs text-slate-400 mt-0.5">Passenger pays cash. Commission deducted from driver wallet.</p>
                </div>
                {paymentMethod === "COD" && (
                  <div className="bg-emerald-500 text-slate-950 rounded-full p-1 self-center ml-2"><FaCheck size={10} /></div>
                )}
              </div>
            </div>

            {/* WALLET */}
            <div
              className={`border rounded-xl p-4 transition-all duration-200 ${
                !bookingDetails?.user
                  ? "opacity-50 cursor-not-allowed border-slate-800 bg-slate-950/30"
                  : !isWalletSufficient
                  ? "border-slate-800 bg-slate-900/20 cursor-not-allowed"
                  : paymentMethod === "WALLET"
                  ? "border-blue-500 bg-blue-950/20"
                  : "border-slate-800 bg-slate-900/50 hover:border-slate-700 cursor-pointer"
              }`}
              onClick={() => {
                if (isWalletSufficient) {
                  setPaymentMethod("WALLET");
                  setIsRazorpaySimulated(false);
                }
              }}
            >
              <div className="flex items-start">
                <div className={`p-2.5 rounded-lg border mr-4 ${
                  !isWalletSufficient
                    ? "bg-slate-800/40 border-slate-700/35 text-slate-500"
                    : "bg-blue-500/10 border-blue-500/20 text-blue-400"
                }`}>
                  <FaWallet size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`font-semibold text-sm ${!isWalletSufficient ? "text-slate-500" : "text-slate-100"}`}>
                      {walletLabel}
                    </h4>
                    <span className="text-[9px] font-bold text-blue-400 bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded tracking-wider uppercase flex items-center gap-1">
                      <FaShieldAlt size={7} /> OTP Secured
                    </span>
                  </div>
                  {(bookingDetails?.user || bookingDetails?.corporateMemberId) ? (
                    <div className="mt-1 flex flex-col gap-1">
                      <p className="text-xs text-slate-400">{walletDescription}</p>
                      <span className={`text-xs font-bold ${isWalletSufficient ? "text-emerald-400" : "text-rose-500"}`}>
                        Wallet Balance: ₹{passengerWalletBalance.toFixed(2)}
                      </span>
                      {!isWalletSufficient && (
                        <span className="text-[10px] text-rose-400 bg-rose-950/30 border border-rose-900/40 px-2 py-0.5 rounded mt-1 inline-flex items-center gap-1.5 w-fit font-medium">
                          <FaExclamationTriangle size={10} /> Insufficient Balance
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">Not available for guest bookings</p>
                  )}
                </div>
                {paymentMethod === "WALLET" && isWalletSufficient && (
                  <div className="bg-blue-500 text-slate-950 rounded-full p-1 self-center ml-2"><FaCheck size={10} /></div>
                )}
              </div>
            </div>

            {/* ONLINE / QR */}
            <div
              className={`border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                paymentMethod === "ONLINE"
                  ? "border-violet-500 bg-violet-950/20"
                  : "border-slate-800 bg-slate-900/50 hover:border-slate-700"
              }`}
              onClick={() => setPaymentMethod("ONLINE")}
            >
              <div className="flex items-start">
                <div className="bg-violet-500/10 p-2.5 rounded-lg border border-violet-500/20 text-violet-400 mr-4">
                  <FaQrcode size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-100 text-sm">Razorpay Online Payment (QR Code)</h4>
                  <p className="text-xs text-slate-400 mt-0.5">Generate dynamic QR for passenger to scan and pay.</p>
                </div>
                {paymentMethod === "ONLINE" && (
                  <div className="bg-violet-500 text-slate-950 rounded-full p-1 self-center ml-2"><FaCheck size={10} /></div>
                )}
              </div>
            </div>
          </div>

          {/* ── Wallet OTP Section ──────────────────────────────────────────────── */}
          {paymentMethod === "WALLET" && isWalletSufficient && (
            <div className="bg-blue-950/20 border border-blue-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FaShieldAlt className="text-blue-400" size={14} />
                <span className="text-sm font-bold text-blue-300">Wallet OTP Verification</span>
              </div>

              {!otpRequested ? (
                /* Step 1: Request OTP */
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Click <span className="text-blue-300 font-semibold">Request OTP</span> to send a secure 6-digit code — {otpRecipientNote}. Ask them to share it with you.
                  </p>
                  {otpError && (
                    <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-900/40 rounded-lg px-3 py-2 flex items-center gap-2">
                      <FaExclamationTriangle size={10} /> {otpError}
                    </p>
                  )}
                  <button
                    onClick={handleRequestOtp}
                    disabled={otpLoading}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-all"
                  >
                    {otpLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending OTP to passenger...
                      </>
                    ) : (
                      <><FaShieldAlt size={12} /> Request OTP for Passenger</>
                    )}
                  </button>
                </div>
              ) : (
                /* Step 2: Enter OTP */
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-emerald-400 font-semibold flex items-center gap-1.5">
                      <FaCheck size={10} /> OTP sent to passenger's app
                    </p>
                    {otpCountdown > 0 ? (
                      <span className="text-xs text-slate-400 tabular-nums">Resend in {otpCountdown}s</span>
                    ) : (
                      <button
                        onClick={handleRequestOtp}
                        disabled={otpLoading}
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors disabled:opacity-50"
                      >
                        <FaSync size={9} /> Resend OTP
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-slate-400">Ask the passenger to open their app and share the OTP displayed on screen.</p>

                  {/* OTP digit input */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-slate-300">Enter OTP from Passenger</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={walletOtp}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setWalletOtp(val);
                        setOtpError("");
                      }}
                      placeholder="000000"
                      className="w-full bg-slate-900 border border-slate-700 focus:border-blue-500 text-white text-center text-3xl font-black tracking-[0.5em] rounded-xl px-4 py-3 outline-none transition-all placeholder:text-slate-700 placeholder:text-xl placeholder:tracking-widest"
                    />
                    <div className="flex justify-between items-center">
                      {walletOtp.length === 6 ? (
                        <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                          <FaCheck size={8} /> OTP entered — ready to confirm
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-500">
                          {walletOtp.length > 0 ? `${6 - walletOtp.length} digits remaining` : "Enter the 6-digit OTP"}
                        </p>
                      )}
                    </div>
                  </div>

                  {otpError && (
                    <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-900/40 rounded-lg px-3 py-2 flex items-center gap-2">
                      <FaExclamationTriangle size={10} /> {otpError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── QR Code (ONLINE) ──────────────────────────────────────────────── */}
          {paymentMethod === "ONLINE" && (
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 mb-6 flex flex-col items-center">
              <span className="text-xs font-bold text-violet-400 uppercase tracking-widest mb-3">Scan to Pay</span>
              <div className={`bg-white p-3 rounded-lg shadow-inner mb-4 flex items-center justify-center relative transition-all duration-300 ${isRazorpaySource ? "w-[260px] h-[380px]" : "w-[204px] h-[204px]"}`}>
                {qrLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <svg className="animate-spin h-8 w-8 text-violet-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Generating QR...</span>
                  </div>
                ) : qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="Payment QR" className={`object-contain ${isRazorpaySource ? "w-full h-full" : "w-[180px] h-[180px]"}`} />
                ) : (
                  <div className="text-rose-500 text-xs text-center p-2">Failed to load QR Code</div>
                )}
              </div>
              <p className="text-center text-xs text-slate-400 max-w-[240px] leading-relaxed">
                Scan using any UPI app to pay <span className="font-bold text-white">₹{amountToCollect.toFixed(2)}</span>
              </p>
              {isRazorpaySource ? (
                <div className="mt-2.5 text-[10px] text-slate-500 font-semibold tracking-wider uppercase">
                  Powered by <span className="text-violet-400 font-bold ml-1">Razorpay QR</span>
                </div>
              ) : (
                <div className="mt-2.5 text-[10px] text-slate-500 font-semibold tracking-wider uppercase select-all">
                  UPI ID: <span className="text-violet-400 font-bold ml-1">{companyUpiId}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsRazorpaySimulated(true)}
                className={`mt-4 w-full py-2.5 rounded-lg text-xs font-semibold border transition-all duration-200 ${
                  isRazorpaySimulated
                    ? "bg-emerald-950/20 border-emerald-500/30 text-emerald-400 cursor-default"
                    : "bg-violet-600 border-violet-500 text-white hover:bg-violet-700"
                }`}
                disabled={isRazorpaySimulated}
              >
                {isRazorpaySimulated
                  ? <span className="flex items-center justify-center gap-1.5"><FaCheck size={10} /> Payment Verified Successfully</span>
                  : "✓ I Confirm Payment Was Received"
                }
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-800 text-slate-300 rounded-xl hover:bg-slate-800 hover:text-white transition-all disabled:opacity-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-semibold shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              disabled={isConfirmDisabled}
            >
              {isSubmitting ? "Confirming..." : "Confirm Payment"}
            </button>
          </div>

          {/* Warning Banner */}
          <div className="mt-4 p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-start gap-3">
            <div className="text-amber-500 mt-0.5 shrink-0"><FaExclamationTriangle size={14} /></div>
            <p className="text-[11px] text-slate-400 leading-normal">
              {paymentMethod === "COD" && "Ensure you have collected cash from the passenger before confirming."}
              {paymentMethod === "WALLET" && !otpRequested && `Click 'Request OTP' — ${otpRecipientNote}.`}
              {paymentMethod === "WALLET" && otpRequested && `Confirming will debit ₹${amountToCollect.toFixed(2)} from the ${isCompanyPaid ? 'company account' : `${passengerName}'s wallet`} and credit your earnings.`}
              {paymentMethod === "ONLINE" && "Verify the payment success screen on passenger's device before confirming."}
            </p>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default CODPaymentModal;
